import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isMobile, setIsMobile] = useState(false);
  
  const { login, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) return;

    try {
      await login(email, password);
      navigate('/user/dashboard'); // Redirect to dashboard on success
    } catch (err) {
      // Error is handled in AuthContext
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Oracle Gaming</title>
        <meta name="description" content="Login to your Oracle Gaming account to access your orders, rewards, and more." />
      </Helmet>
      <Header />
      <div className="h-20" />
      <section className="gallery-carousel" style={{ paddingTop: isMobile ? '220px' : '120px', paddingBottom: isMobile ? '40px' : '60px' }}>
        <div className="container">
          <h2 className="h2 section-title" style={{ color: 'var(--white)', marginBottom: isMobile ? '24px' : '40px' }}>LOGIN</h2>
          
          <div style={{ maxWidth: '500px', margin: '0 auto', width: '100%' }}>
            <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: isMobile ? '30px 20px' : '40px 24px' }}>
              <p className="text-center mb-6" style={{ color: 'var(--platinum)' }}>
                Access your account and start earning rewards!
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500" style={{ color: '#f48771', marginBottom: '24px' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="email" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: errors.email ? '1px solid #f48771' : '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p style={{ marginTop: '8px', fontSize: '14px', color: '#f48771' }}>{errors.email}</p>}
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="password" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: errors.password ? '1px solid #f48771' : '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="••••••••"
                  />
                  {errors.password && <p style={{ marginTop: '8px', fontSize: '14px', color: '#f48771' }}>{errors.password}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                  style={{
                    width: '100%',
                    marginBottom: '20px',
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Logging in...
                    </span>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>

              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <p style={{ color: 'var(--platinum)', fontSize: '14px', marginBottom: '12px' }}>
                  Don't have an account?{' '}
                  <Link to="/register" className="btn-link" style={{ color: 'var(--orange)' }}>
                    Register here
                  </Link>
                </p>
                <Link to="/shop" style={{ color: 'var(--platinum)', fontSize: '14px' }}>
                  Continue as guest
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

